// src/pages/PickMuse.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MuseCard from './MuseCard';
import { useAuth } from '../../auth/contexts/AuthContext';
import { PACKS } from '../../../data/packs';
import { Muse } from '../../../types';
import '../styles/PickMuse.css';

interface PickMuseProps {
  onMuseSelect?: (muse: Muse) => void;
}

const starterMuses: Muse[] = [
  PACKS['Night Pack'].muses[0],
  PACKS['Sun Pack'].muses[0],
  PACKS['Forest Pack'].muses[0],
  PACKS['Cyclops Pack'].muses[0],
  PACKS['Friendly Pack 1'].muses[0],
];

const PickMuse: React.FC<PickMuseProps> = ({ onMuseSelect }) => {
  const { userData, currentUser, updateUserData } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedMuse, setSelectedMuse] = useState<Muse | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userData) {
      if (userData.selectedMuse) {
        navigate('/app/market');
      } else {
        setLoading(false);
      }
    } else if (currentUser === null) {
      // No user logged in
      setLoading(false);
    }
  }, [navigate, userData, currentUser]);

  const pickMuse = async (muse: Muse): Promise<void> => {
    if (!userData || !currentUser) return;
    
    try {
      setSelectedMuse(muse);
      
      // Update selected muse using Firestore directly
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../../../shared/storage/firebase');
      
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, { selectedMuse: muse });
      
      // Update local state immediately for instant UI refresh
      updateUserData({ selectedMuse: muse });
      
      if (onMuseSelect) {
        onMuseSelect(muse);
      }
      
      // Add a small delay before navigation for better UX
      setTimeout(() => navigate('/app/market'), 1000);
    } catch (error) {
      console.error('Error selecting muse:', error);
      void window.appAlert('Failed to select muse. Please try again.');
      setSelectedMuse(null);
    }
  };

  if (loading) {
    return (
      <div className="container text-center mt-5">
        <div className="spin" style={{ fontSize: '2rem' }}>🔄</div>
        <p className="mt-3">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h2 className="text-center mb-4">Choose Your First Muse</h2>
        <p className="text-center mb-4">
          Select your starting muse! This will be your companion throughout your journey.
        </p>

        <div className="grid grid-3">
          {starterMuses.map((m, i) => (
            <div 
              key={i} 
              className="game-card"
              onClick={() => !selectedMuse && pickMuse(m)}
              style={{
                cursor: selectedMuse ? 'default' : 'pointer',
                opacity: selectedMuse && selectedMuse !== m ? 0.5 : 1,
                transform: selectedMuse === m ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}
            >
              <div className="game-card-header">
                <img 
                  src={m.img} 
                  alt={m.name} 
                  style={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    marginRight: 8, 
                    verticalAlign: 'middle' 
                  }} 
                />
                {m.name} <span style={{ color: '#888', marginLeft: 8 }}>{m.rarity}</span>
              </div>
              <div className="game-card-body text-center">
                <MuseCard
                  img={m.img}
                  name={m.name}
                  rarity={m.rarity}
                  size="large"
                />
                {selectedMuse?.name === m.name && (
                  <div className="mt-3 text-success">
                    Selected! Redirecting...
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PickMuse;
